import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Clock, Flame, Plus, ArrowRight } from "lucide-react";
import { FoodCustomizationModal } from "./FoodCustomizationModal";
import { useApp } from "../App";
import { getAllMenuItems } from "@/services/menuService";
import { MenuItem } from "@/types";
import { getMenuItemImageUrl } from "@/services/utlits";
import { CartNotification } from "./CartNotification";
import { useRestaurantStatus } from "@/hooks/useRestaurantStatus";

export function HeroBanner() {
  const { addToCart, restaurant } = useApp();
  const { isClosed, message } = useRestaurantStatus(restaurant);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const [showCartToast, setShowCartToast] = useState(false);
  const [cartItemName, setCartItemName] = useState("");
  const [cartItemQty, setCartItemQty] = useState(1);

  // Get popular items for showcase
  const popularItems = menuItems.slice(0, 4);
  const fetchMenu = async () => {
    try {
      const response = await getAllMenuItems();
      setMenuItems(response);
    } catch (err) {
      console.log(err);
    }
  };
  // Load items on screen mount
  useEffect(() => {
    fetchMenu();
  }, []);

  const handleQuickAdd = (item: any) => {
    setSelectedItem(item);
    setIsCustomizationOpen(true);

    // Push new history state so back button closes modal
    window.history.pushState({ modal: true }, "");
  };

  const handleCustomizedAdd = (customizedItem: any) => {
    addToCart(customizedItem);
    setIsCustomizationOpen(false);
    setSelectedItem(null);
    setShowCartToast(true);
    setCartItemName(customizedItem?.name);
    setCartItemQty(customizedItem?.quantity);
  };
  // Stack history maintaine
  useEffect(() => {
    const handleBack = () => {
      if (isCustomizationOpen) {
        setIsCustomizationOpen(false);
        setSelectedItem(null);
        return;
      }
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [isCustomizationOpen]);
  return (
    <div className="relative min-h-fit sm:min-h-[500px] md:min-h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Thai Food Spread"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
      </div>

      <div className="relative max-w-7xl mx-auto mobile-padding h-full flex flex-col justify-center py-8 sm:py-12">
        {/* Hero Text */}
        <div className="text-white max-w-3xl mb-6 sm:mb-8 animate-slide-in-left">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight font-display">
            Delicious Pizza & Crispy Wings
            <span className="block text-thai-300 bg-gradient-to-r from-thai-300 to-yellow-300 bg-clip-text text-transparent">
              Freshly Prepared, Delivered Fast
            </span>
          </h1>
          <p
            className="text-sm sm:text-base md:text-lg text-gray-200 mb-4 sm:mb-6 leading-relaxed animate-slide-in-left"
            style={{ animationDelay: "0.2s" }}
          >
            Enjoy mouthwatering pizzas and perfectly seasoned wings, crafted
            fresh with quality ingredients and bold flavors you’ll love.
          </p>

          {/* Quick Stats */}
          <div
            className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm animate-slide-in-left"
            style={{ animationDelay: "0.4s" }}
          >
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-emerald-400 rounded-full"></div>
              <span className="font-medium">Fresh Ingredients Daily</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-thai-400 rounded-full"></div>
              <span className="font-medium">Traditional Recipes</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full"></div>
              <span className="font-medium">
                Fast & Reliable Pickup / Delivery
              </span>
            </div>
          </div>
        </div>

        {/* Food Showcase Section */}
        <div
          className="hidden md:block bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 shadow-xl animate-slide-in-up"
          style={{ animationDelay: "0.6s" }}
        >
          {/* Section Header */}
          <div className="text-center mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 font-display">
              Order Now - Popular Dishes
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm">
              Customer favorites - order with one click
            </p>
          </div>

          {/* Food Items Grid */}
          {/* <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-4"> */}
          {/* Desktop (lg+) → 8 items */}
          <div className="hidden lg:grid grid-cols-4 gap-3 sm:gap-4 mb-4">
            {menuItems
              .filter((item) => item.isFeatured) // Only featured items
              .slice(0, 8) // Limit to top 8
              .map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
                  style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                >
                  {/* Item Image */}
                  {/* <div className="relative h-24 sm:h-32 overflow-hidden">
                    <img
                      src={getMenuItemImageUrl(item?.imageUrl)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1 right-1">
                      <Badge className="bg-emerald-600 text-white text-xs px-1.5 py-0.5">
                        ${item?.discountPrice.toFixed(2)}
                      </Badge>
                    </div>
                    {item.isPopular && (
                      <div className="absolute top-1 left-1">
                        <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0.5">
                          <Star className="w-2 h-2 mr-0.5 fill-current" />
                          Hot
                        </Badge>
                      </div>
                    )}
                  </div> */}
                  <div className="relative h-24 sm:h-32 overflow-hidden">
                    <img
                      src={getMenuItemImageUrl(item?.imageUrl)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />

                    {/* Price badges (right side, in a row) */}
                    <div className="absolute top-1 right-1 flex flex-row items-center space-x-1">
                      {item?.discountPrice &&
                      item?.discountPrice < item?.basePrice &&
                      item?.discountPrice != item?.basePrice ? (
                        <>
                          {/* Discounted Price */}
                          <Badge className="bg-emerald-600 text-white text-sm px-1.5 py-0.5">
                            ${item.discountPrice.toFixed(2)}
                          </Badge>
                          {/* Original Price (strikethrough) */}
                          <Badge className="bg-gray-300 text-gray-700 text-xs px-1.5 py-0.5 line-through">
                            ${item.basePrice.toFixed(2)}
                          </Badge>
                        </>
                      ) : (
                        <Badge className="bg-emerald-600 text-white text-xs px-1.5 py-0.5">
                          ${item.basePrice.toFixed(2)}
                        </Badge>
                      )}
                    </div>

                    {/* Popular badge (left side) */}
                    {item.isPopular && (
                      <div className="absolute top-1 left-1">
                        <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0.5 flex items-center">
                          <Star className="w-2 h-2 mr-0.5 fill-current" />
                          Hot
                        </Badge>
                      </div>
                    )}
                    <div className="absolute bottom-1 left-1 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex items-center gap-1 bg-black/60 backdrop-blur-lg px-2.5 py-1 rounded-lg border border-white/10 shadow-md">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
                        <span className="text-[10px] sm:text-xs text-white font-medium tracking-wide">
                          Image for illustration only
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Item Content */}
                  <div className="p-2 sm:p-3">
                    <h3 className="font-semibold text-xs sm:text-sm text-gray-900 mb-1 line-clamp-1">
                      {item.name}
                    </h3>

                    {/* Item Badges */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {item.isSpicy && (
                        <Badge
                          variant="outline"
                          className="text-xs border-red-200 text-red-700 bg-red-50 px-1 py-0"
                        >
                          <Flame className="w-2 h-2" />
                        </Badge>
                      )}
                      {/* {item.preparationTime && (
                        <Badge
                          variant="outline"
                          className="text-xs border-gray-200 text-gray-600 bg-gray-50 px-1 py-0"
                        >
                          {item.preparationTime}m
                        </Badge>
                      )} */}
                    </div>

                    {/* Add to Cart Button */}
                    <Button
                      onClick={() => handleQuickAdd(item)}
                      disabled={isClosed}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-7 sm:h-8 text-xs rounded-md transition-all duration-200"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              ))}
          </div>
          {/* Mobile (<md) → 4 items */}
          <div className="grid md:hidden grid-cols-2 gap-3 sm:gap-4 mb-4">
            {menuItems
              .filter((item) => item.isFeatured) // Only featured items
              .slice(0, 4) // Limit to top 8
              .map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
                  style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                >
                  {/* Item Image */}
                  {/* <div className="relative h-24 sm:h-32 overflow-hidden">
                    <img
                      src={getMenuItemImageUrl(item?.imageUrl)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1 right-1">
                      <Badge className="bg-emerald-600 text-white text-xs px-1.5 py-0.5">
                        ${item?.discountPrice.toFixed(2)}
                      </Badge>
                    </div>
                    {item.isPopular && (
                      <div className="absolute top-1 left-1">
                        <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0.5">
                          <Star className="w-2 h-2 mr-0.5 fill-current" />
                          Hot
                        </Badge>
                      </div>
                    )}
                  </div> */}
                  <div className="relative h-24 sm:h-32 overflow-hidden">
                    <img
                      src={getMenuItemImageUrl(item?.imageUrl)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />

                    {/* Price badges (right side, in a row) */}
                    <div className="absolute top-1 right-1 flex flex-row items-center space-x-1">
                      {item?.discountPrice &&
                      item?.discountPrice < item?.basePrice &&
                      item?.discountPrice != item?.basePrice ? (
                        <>
                          {/* Discounted Price */}
                          <Badge className="bg-emerald-600 text-white text-sm px-1.5 py-0.5">
                            ${item.discountPrice.toFixed(2)}
                          </Badge>
                          {/* Original Price (strikethrough) */}
                          <Badge className="bg-gray-300 text-gray-700 text-xs px-1.5 py-0.5 line-through">
                            ${item.basePrice.toFixed(2)}
                          </Badge>
                        </>
                      ) : (
                        <Badge className="bg-emerald-600 text-white text-xs px-1.5 py-0.5">
                          ${item.basePrice.toFixed(2)}
                        </Badge>
                      )}
                    </div>

                    {/* Popular badge (left side) */}
                    {item.isPopular && (
                      <div className="absolute top-1 left-1">
                        <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0.5 flex items-center">
                          <Star className="w-2 h-2 mr-0.5 fill-current" />
                          Hot
                        </Badge>
                      </div>
                    )}
                    <div className="absolute bottom-1 left-1 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex items-center gap-1 bg-black/60 backdrop-blur-lg px-2.5 py-1 rounded-lg border border-white/10 shadow-md">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
                        <span className="text-[10px] sm:text-xs text-white font-medium tracking-wide">
                          Image for illustration only
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Item Content */}
                  <div className="p-2 sm:p-3">
                    <h3 className="font-semibold text-xs sm:text-sm text-gray-900 mb-1 line-clamp-1">
                      {item.name}
                    </h3>

                    {/* Item Badges */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {item.isSpicy && (
                        <Badge
                          variant="outline"
                          className="text-xs border-red-200 text-red-700 bg-red-50 px-1 py-0"
                        >
                          <Flame className="w-2 h-2" />
                        </Badge>
                      )}
                      {/* {item.preparationTime && (
                        <Badge
                          variant="outline"
                          className="text-xs border-gray-200 text-gray-600 bg-gray-50 px-1 py-0"
                        >
                          {item.preparationTime}m
                        </Badge>
                      )} */}
                    </div>

                    {/* Add to Cart Button */}
                    <Button
                      onClick={() => handleQuickAdd(item)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-7 sm:h-8 text-xs rounded-md transition-all duration-200"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              ))}
          </div>

          {/* View Full Menu CTA */}
          <div className="text-center">
            <Button
              onClick={() => {
                const menuSection = document.getElementById("menu-section");
                if (menuSection) {
                  menuSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
              variant="outline"
              className="px-4 sm:px-6 py-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50 text-sm font-medium"
            >
              View Full Menu
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Food Customization Modal */}
      {selectedItem && (
        <FoodCustomizationModal
          item={selectedItem}
          isOpen={isCustomizationOpen}
          onClose={() => {
            setIsCustomizationOpen(false);
            setSelectedItem(null);
            // Remove the pushed state
            if (window.history.state?.modal) {
              window.history.back();
            }
          }}
          onAddToCart={handleCustomizedAdd}
        />
      )}
      {/* Item added notification */}
      <CartNotification
        isVisible={showCartToast}
        itemName={cartItemName}
        quantity={cartItemQty}
        onClose={() => setShowCartToast(false)}
      />
    </div>
  );
}
